import React from 'react';
import { Text } from '@spark';

const fallbackName = 'Untitled';

export const normalizeItemNames = (names = []) =>
    names.map((name) => String(name || '').trim() || fallbackName);

/**
 * "A"; "A and B"; "A, B, and C"
 */
export const formatNameConjunction = (names = []) => {
    const items = normalizeItemNames(names);
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const BoldName = ({ children, variant = 'base-semibold' }) => (
    <Text variant={variant} as="strong">
        {children}
    </Text>
);

const NAMED_MOVE_LIMIT = 3;

const joinBoldNames = (names, { lastAnd = true, variant = 'base-semibold' } = {}) => {
    if (names.length === 1) return <BoldName variant={variant}>{names[0]}</BoldName>;
    return names.map((name, index) => {
        const node = <BoldName key={`${name}-${index}`} variant={variant}>{name}</BoldName>;
        if (index === 0) return <React.Fragment key={`${name}-${index}`}>{node}</React.Fragment>;
        const separator = lastAnd && index === names.length - 1 ? ' and ' : ', ';
        return (
            <React.Fragment key={`${name}-${index}`}>
                {separator}
                {node}
            </React.Fragment>
        );
    });
};

/**
 * Modal titles inherit Spark's lead-semibold heading. Wrap the sentence in
 * lead-regular so "Move", commas, "to the", and "project" stay light, and
 * keep only task / project names on lead-bold.
 */
export const renderMoveTitle = (names = [], destName = null, itemCount = 0) => {
    const items = normalizeItemNames(names);
    const count = Math.max(itemCount, items.length);
    const named = items.slice(0, NAMED_MOVE_LIMIT);
    const extra = Math.max(0, count - named.length);
    const titleBold = 'lead-bold';

    let subject;
    if (named.length === 0) {
        subject = count === 1 ? 'this task' : `${count} tasks`;
    } else if (extra > 0) {
        subject = (
            <>
                {joinBoldNames(named, { lastAnd: false, variant: titleBold })}
                {` and ${extra} more ${extra === 1 ? 'task' : 'tasks'}`}
            </>
        );
    } else {
        subject = joinBoldNames(named, { lastAnd: true, variant: titleBold });
    }

    return (
        <Text variant="lead-regular">
            Move {subject}
            {destName ? (
                <>
                    {' '}to the <BoldName variant={titleBold}>{destName}</BoldName> project
                </>
            ) : null}
        </Text>
    );
};

export const renderBoldNameList = (names = []) => {
    const items = normalizeItemNames(names);
    if (items.length === 0) return null;
    if (items.length === 1) return <BoldName>{items[0]}</BoldName>;
    if (items.length === 2) {
        return (
            <>
                <BoldName>{items[0]}</BoldName>
                {' and '}
                <BoldName>{items[1]}</BoldName>
            </>
        );
    }
    return items.map((name, index) => {
        const node = <BoldName key={`${name}-${index}`}>{name}</BoldName>;
        if (index === 0) return <React.Fragment key={`${name}-${index}`}>{node}</React.Fragment>;
        if (index === items.length - 1) {
            return (
                <React.Fragment key={`${name}-${index}`}>
                    {', and '}
                    {node}
                </React.Fragment>
            );
        }
        return (
            <React.Fragment key={`${name}-${index}`}>
                {', '}
                {node}
            </React.Fragment>
        );
    });
};

export const bulkDeleteTitle = (count, entityNoun, names = []) => {
    const items = normalizeItemNames(names);
    if (count === 1) {
        const name = items[0] || fallbackName;
        return `Delete ${entityNoun} "${name}"?`;
    }
    return `Delete ${count} ${entityNoun}s?`;
};

export const bulkDeletePrimaryLabel = (count, entityNoun) => {
    const label = entityNoun === 'task' ? 'Task' : entityNoun.charAt(0).toUpperCase() + entityNoun.slice(1);
    return count === 1 ? `Delete ${label}` : `Delete ${label}s`;
};
